import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { locationApi } from '../services/locationApi';

const ISRAEL_CODE = 'IL';

// Geocode a city object via Nominatim when the DB has no coordinates stored.
const geocode = async (cityName, countryName) => {
  try {
    const q = countryName ? `${cityName}, ${countryName}` : cityName;
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
      { headers: { Accept: 'application/json' } }
    );
    const results = await resp.json();
    if (Array.isArray(results) && results.length > 0) {
      return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
    }
  } catch (_) {}
  return null;
};

/**
 * Country + City cascading dropdowns for the Business Profile form.
 *
 * Selecting a city resolves its coordinates (from the DB or via Nominatim) and
 * calls onCityChange(name, lat, lng) so the parent can update the map pin.
 *
 * Rendered as a full-width item inside a TWO_COL grid (gridColumn 1 / -1).
 *
 * Props:
 *   cityName            — controlled city string (formData.businessCity)
 *   accountFallbackCity — account.city for the "using account city" helper hint
 *   onCityChange(name, lat|null, lng|null) — called on every city update
 */
const BusinessLocationPicker = ({ cityName, accountFallbackCity, onCityChange }) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  // Keep a ref to selectedCountry for use inside the async geocode callback
  const selectedCountryRef = useRef(null);

  useEffect(() => {
    locationApi.getAllCountries()
      .then((data) => {
        const list = data || [];
        setCountries(list);
        const israel = list.find((c) => c.code === ISRAEL_CODE) || null;
        setSelectedCountry(israel);
        selectedCountryRef.current = israel;
      })
      .catch(console.error)
      .finally(() => setLoadingCountries(false));
  }, []);

  useEffect(() => {
    if (!selectedCountry) { setCities([]); return; }
    setLoadingCities(true);
    locationApi.getCitiesByCountryId(selectedCountry.id)
      .then((data) => setCities(data || []))
      .catch(console.error)
      .finally(() => setLoadingCities(false));
  }, [selectedCountry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCitySelect = async (city) => {
    if (!city) { onCityChange('', null, null, null, null); return; }
    const name = city.nameEn || city.nameHe || '';
    let lat = parseFloat(city.latitude);
    let lng = parseFloat(city.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      const coords = await geocode(name, selectedCountryRef.current?.nameEn);
      if (coords) { lat = coords.lat; lng = coords.lng; }
    }
    onCityChange(
      name,
      Number.isFinite(lat) ? lat : null,
      Number.isFinite(lng) ? lng : null,
      city.id ?? null,
      selectedCountryRef.current?.id ?? null,
    );
  };

  const cityHelperText = !cityName && accountFallbackCity
    ? t('profile.usingAccountCity', { city: accountFallbackCity })
    : t('profile.cityHelper');

  return (
    <Box sx={{ gridColumn: { md: '1 / -1' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Country */}
      <Autocomplete
        options={countries}
        getOptionLabel={(c) => c.nameEn || ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={selectedCountry}
        onChange={(_, country) => {
          selectedCountryRef.current = country;
          setSelectedCountry(country);
          setCities([]);
        }}
        loading={loadingCountries}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('profile.country')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCountries && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* City */}
      <Autocomplete
        freeSolo
        options={cities}
        getOptionLabel={(c) => (typeof c === 'string' ? c : c.nameEn || c.nameHe || '')}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        inputValue={cityName || ''}
        onInputChange={(_, val, reason) => {
          // 'input' = user typed; 'reset' = option selected (handled by onChange)
          if (reason !== 'reset') onCityChange(val, null, null, null, null);
        }}
        onChange={(_, city) => {
          if (!city) {
            onCityChange('', null, null, null, null);
          } else if (typeof city !== 'string') {
            handleCitySelect(city);
          }
        }}
        loading={loadingCities}
        disabled={!selectedCountry}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('profile.businessCity')}
            helperText={cityHelperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" />,
              endAdornment: (
                <>
                  {loadingCities && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BusinessLocationPicker;
